import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
	Modal,
	ModalHeader,
	ModalBody
} from 'reactstrap';

import { DangerAlert, SuccessAlert } from '@techchange/components';
import { messages } from '../../i18n';

import RuleEditor from '../RuleEditor';

const propTypes = {

	/**
	 * The resource to display.
	 */
	resource: PropTypes.shape({

		/**
		 * The title of the resource.
		 */
		title: PropTypes.string,

		/**
		 * The type of the reosurce
		 */
		type: PropTypes.string,

		/**
		 * Child resources of the resource
		 */
		children: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * Unique ID for the resource
			 */
			id: PropTypes.string,

			/**
			 * Title for the resource
			 */
			title: PropTypes.string,

		})),

	}).isRequired,

	/**
	 * The parent resource.
	 */
	parent: PropTypes.shape({

		/**
		 * The parent resource ID.
		 */
		id: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * The rule and associated tracker for the resource.
	 */
	rule: PropTypes.shape({

		/**
		 * Condition for the rule
		 */
		condition: PropTypes.shape({

			/**
			 * Unique ID for the condition
			 */
			id: PropTypes.string,

			/**
			 * Count of the condition
			 */
			count: PropTypes.number,

			/**
			 * Operator of the condition
			 */
			operator: PropTypes.string,

			/**
			 * Subconditions of the condition
			 */
			subconditions: PropTypes.arrayOf(PropTypes.shape({

				/**
				 * Unique identifier for the subcondition's object
				 */
				object_id: PropTypes.string,

			})),
		}),

	}),

	/**
	 * The current user's ID.
	 */
	userId: PropTypes.string.isRequired,

	/**
	 * The list of gravity forms for the session.
	 */
	gravityForms: PropTypes.arrayOf(PropTypes.shape({

		title: PropTypes.string.isRequired,

		tc_uuid: PropTypes.string.isRequired,

	})),

	/**
	 * True, if there was an error saving the rule editor modal.
	 */
	submitError: PropTypes.bool.isRequired,

	/**
	 * True, if there was success saving the rule editor modal.
	 */
	submitSuccess: PropTypes.bool.isRequired,

	/**
	 * True, if the rule editor modal is visible.
	 */
	isOpen: PropTypes.bool.isRequired,

	/**
	 * Function to handle saving the rule editor modal.
	 */
	onSave: PropTypes.func.isRequired,

	/**
	 * Function to handle toggling the rule editor modal.
	 */
	onToggle: PropTypes.func.isRequired,

};

const defaultProps = {
	isOpen: false,
	submitError: false,
};

const RuleEditorModal = (props) => {

	const {
		resource,
		parent,
		rule,
		userId,
		gravityForms,
		submitError,
		submitSuccess,
		isOpen,
		onSave,
		onToggle,
	} = props;

	return (
		<Modal
			size="lg"
			isOpen={isOpen}
			toggle={onToggle}>
			<ModalHeader
				tag="h2"
				toggle={onToggle}>
				{rule &&
					<FormattedMessage
						{...messages.TC_COMPONENTS_RULE_EDITOR_MODAL_HEADER_TEXT_EDIT}
						values={{ title: resource.title }} />
				}
				{!rule &&
					<FormattedMessage
						{...messages.TC_COMPONENTS_RULE_EDITOR_MODAL_HEADER_TEXT_CREATE}
						values={{ title: resource.title }} />
				}
			</ModalHeader>
			<ModalBody>
				{submitError &&
					<DangerAlert>
						<FormattedMessage
							{...messages.TC_COMPONENTS_RULE_EDITOR_MODAL_ERROR_MSG_SAVE_RULE} />
					</DangerAlert>
				}
				{submitSuccess &&
					<SuccessAlert>
						<FormattedMessage
							{...messages.TC_COMPONENTS_RULE_EDITOR_MODAL_SUCCESS_MSG_SAVE_RULE} />
					</SuccessAlert>
				}
				<RuleEditor
					resource={resource}
					parent={parent}
					rule={rule}
					userId={userId}
					gravityForms={gravityForms}
					onCancel={onToggle}
					onSave={onSave} />
			</ModalBody>
		</Modal>
	);
};

RuleEditorModal.propTypes = propTypes;
RuleEditorModal.defaultProps = defaultProps;

export default RuleEditorModal;
